import { describeFieldRules } from './fieldValidation'
import type { ProcessFieldDef } from '@/api/processStepsApi'

/**
 * Naming and summarising an attribute DEFINITION.
 *
 * Kept out of the dialog because the key derivation is the load-bearing bit: an
 * author types a label, and the machine `key` is derived once, on add, and frozen
 * from then on. Stored values are keyed on it, so a rename after the fact would
 * orphan every value silently — which is why the API refuses to change it and
 * why the UI never offers to.
 */

/**
 * `Spin speed (RPM)` → `spinSpeed`.
 *
 * Lower camel, letters and digits only. Returns '' when the label has nothing
 * usable in it, which the caller treats as "not ready to save" rather than
 * inventing a key nobody can read.
 */
export function keyFromLabel(label: string): string {
  const words = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (words.length === 0) return ''
  const [first, ...rest] = words
  // A leading digit would make the key unusable as an identifier anywhere it is
  // read back, so it is prefixed rather than silently dropped.
  const head = /^\d/.test(first) ? `a${first}` : first
  return head + rest.map((w) => w[0].toUpperCase() + w.slice(1)).join('')
}

/** The "Options / rules" cell: an enum lists its options, anything else its rules. */
export function summariseDef(def: ProcessFieldDef): string | null {
  if (def.dataType === 'enum') {
    return def.options.length > 0 ? def.options.join(', ') : null
  }
  return describeFieldRules(def)
}
